import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Tabs, 
  Tab, 
  Typography, 
  IconButton, 
  Tooltip,
  Alert
} from '@mui/material';
import { 
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { Highlight, themes } from 'prism-react-renderer';
import { useThemeMode } from '../../contexts/ThemeContext';

const LiveEditor = ({ 
  code, 
  component: Component, 
  title,
  description 
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [copied, setCopied] = useState(false);
  const { isDark } = useThemeMode();

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        border: '1px solid', 
        borderColor: 'gray.g200',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        borderBottom: '1px solid', 
        borderColor: 'gray.g200',
        backgroundColor: 'gray.g25'
      }}>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="gray.g600" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          )}
        </Box>
        <Tabs 
          value={activeTab} 
          onChange={(_, v) => setActiveTab(v)}
          sx={{ minHeight: 40 }}
        >
          <Tab 
            label="Preview" 
            sx={{ 
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 500
            }} 
          />
          <Tab 
            label="Code" 
            sx={{ 
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 500
            }} 
          />
        </Tabs>
      </Box>

      {/* Content */}
      {activeTab === 0 && (
        <Box sx={{ p: 4, minHeight: 200 }}>
          <Component />
        </Box>
      )}

      {activeTab === 1 && (
        <Box sx={{ position: 'relative' }}>
          {/* Code Actions */}
          <Box sx={{ 
            position: 'absolute', 
            top: 12, 
            right: 12, 
            zIndex: 1,
            display: 'flex',
            gap: 1
          }}>
            <Tooltip title="Copy code">
              <IconButton
                size="small"
                onClick={handleCopyCode}
                sx={{
                  backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                  color: isDark ? 'white' : 'inherit',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                  }
                }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Code Display */}
          <Highlight
            theme={isDark ? themes.vsDark : themes.github}
            code={code}
            language="jsx"
          >
            {({ className, style, tokens, getLineProps, getTokenProps }) => (
              <pre 
                className={className} 
                style={{ 
                  ...style, 
                  margin: 0,
                  padding: '24px',
                  fontSize: '14px',
                  lineHeight: 1.5,
                  overflow: 'auto',
                  maxHeight: '400px'
                }}
              >
                {tokens.map((line, i) => (
                  <div key={i} {...getLineProps({ line, key: i })}>
                    <span style={{ 
                      color: isDark ? '#9CA3AF' : '#6B7280',
                      marginRight: '16px',
                      userSelect: 'none',
                      minWidth: '24px',
                      display: 'inline-block'
                    }}>
                      {i + 1}
                    </span>
                    {line.map((token, key) => (
                      <span key={key} {...getTokenProps({ token, key })} />
                    ))}
                  </div>
                ))}
              </pre>
            )}
          </Highlight>

          {/* Copy Success Alert */}
          {copied && (
            <Alert 
              severity="success" 
              sx={{ 
                position: 'absolute',
                top: 60,
                right: 12,
                width: 'auto',
                minWidth: 120
              }}
            >
              Code copied!
            </Alert>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default LiveEditor;